import { DateTime } from "luxon";
import {
  ACTION_APPROVE,
  ACTION_CANCEL,
  ACTION_CANCEL_APPROVE,
  ACTION_CANCEL_REFUSE,
  ACTION_CANCEL_SUBMIT,
  ACTION_REFUSE,
  ACTION_REINSTATE,
  ACTION_RETRACT,
  EAD_TIME_OFF_TYPE,
  EDU_TIME_OFF_TYPE,
  IAM_CONTRACT,
} from "./constants";
import {
  isDateTimeGreaterThanTwoMonthsInPast,
  isDateTimeInPast,
  isDateTimeWithinOneHour,
  ONE_YEAR_FROM,
  THREE_YEARS_IN_PAST,
} from "./dateHelper";
import { setEndDateTime, setStartDateTime } from "./inputValidation";
import {
  END_DATE_IS_REQUIRED,
  INVALID_END_DATE,
  INVALID_START_DATE,
  SHIFT_TIME_IS_REQUIRED,
  START_DATE_CANNOT_BE_IN_THE_PAST,
  START_DATE_IS_REQUIRED,
  START_DATE_MUST_MATCH_END_DATE,
  UNKNOWN_ACTION_MESSAGE,
  YOU_CANNOT_APPROVE_CANCEL_TOR_PAST_DEADLINE_MESSAGE,
  YOU_CANNOT_APPROVE_CANCEL_TOR_PAST_DEADLINE_START_TIME_OF_SHIFT_MESSAGE,
  YOU_CANNOT_APPROVE_TOR_PAST_DEADLINE_MESSAGE,
  YOU_CANNOT_APPROVE_TOR_PAST_DEADLINE_START_TIME_OF_SHIFT_MESSAGE,
  YOU_CANNOT_APPROVE_YOUR_OWN_TOR,
  YOU_CANNOT_APPROVE_YOUR_OWN_TOR_CANCELLATION,
  YOU_CANNOT_CANCEL_AN_APPROVED_EAD_EDY_MESSAGE,
  YOU_CANNOT_CANCEL_TOR_OVER_TWO_MONTHS_IN_PAST,
  YOU_CANNOT_CANCEL_TOR_PAST_DEADLINE_ONE_HOUR_BEFORE_SHIFT_MESSAGE,
  YOU_CANNOT_CANCEL_TOR_PAST_SHIFT_END_TIME_MESSAGE,
  YOU_CANNOT_CANCEL_YOUR_OWN_TOR_FROM_TOR_ADMIN_SCREEN,
  YOU_CANNOT_REFUSE_CANCEL_TOR_PAST_DEADLINE_MESSAGE,
  YOU_CANNOT_REFUSE_CANCEL_TOR_PAST_DEADLINE_START_TIME_OF_SHIFT_MESSAGE,
  YOU_CANNOT_REFUSE_TOR_PAST_DEADLINE_MESSAGE,
  YOU_CANNOT_REFUSE_TOR_PAST_DEADLINE_START_TIME_OF_SHIFT_MESSAGE,
  YOU_CANNOT_REFUSE_YOUR_OWN_TOR,
  YOU_CANNOT_REFUSE_YOUR_OWN_TOR_CANCELLATION,
  YOU_CANNOT_REINSTATE_TOR_OVER_TWO_MONTHS_IN_PAST,
  YOU_CANNOT_REINSTATE_YOUR_OWN_TOR,
  YOU_CANNOT_RETRACT_TOR_PAST_DEADLINE_ONE_HOUR_BEFORE_SHIFT_MESSAGE,
  YOU_CANNOT_RETRACT_TOR_PAST_SHIFT_END_TIME_MESSAGE,
} from "./messageConstants";
import {
  isHourSubmissionDeadlineRequired,
  isShiftStartSubmissionDeadlineRequired,
  isTwoMonthsFromShiftStartSubmissionDeadlineRequired,
} from "./timeOffRequestEventHelper";
import {
  isEADorEDUorSEAD,
  isLWOPTOWOPRequest,
  isWOPPastDeadline,
  setTimeToMidnightForEADorEDUorSEAD,
  TOR_SUBMISSION_CUTOFF_TIME_REMINDER,
} from "./timeOffRequestUtils";
import { getTimeZoneFromSwaStation } from "./timeZoneService";
import { getUnionContractGivenWorkgroup, getWorkgroupGivenJobCode } from "./workgroupMapper";
import { set_time_to_midnight } from "./dateTimeFormatter";

export function TOR_validateShiftStartDate(input, station, timeOffType, empUnionContract) {
  let maxDate =
    empUnionContract &&
    timeOffType &&
    TOR_SUBMISSION_CUTOFF_TIME_REMINDER[empUnionContract][timeOffType]
      .getMaxSubmissionShiftDate(station)
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  if (input === null) {
    return START_DATE_IS_REQUIRED;
  }

  const startDateVal = set_time_to_midnight(input);

  var today = DateTime.now()
    .setZone(getTimeZoneFromSwaStation(station))
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  if (startDateVal.toMillis() < today.toMillis() && !isLWOPTOWOPRequest(timeOffType)) {
    return START_DATE_CANNOT_BE_IN_THE_PAST;
  }

  if (maxDate < startDateVal) {
    return INVALID_START_DATE;
  }

  return "";
}

export function validate24HourWindowForUnknownShiftTimes(startDate, endDate, station) {
  if (endDate === null) {
    return END_DATE_IS_REQUIRED;
  }
  if (startDate === null) {
    return "";
  }

  var today = DateTime.now()
    .setZone(getTimeZoneFromSwaStation(station), { keepLocalTime: true })
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  if (startDate.equals(endDate)) {
    if (today.toMillis() >= endDate.toMillis()) {
      return SHIFT_TIME_IS_REQUIRED;
    }
    return "";
  } else {
    return START_DATE_MUST_MATCH_END_DATE;
  }
}

export function TOR_validateShiftEndDate(startDate, input, station, timeOffType, empUnionContract) {
  let maxDate =
    empUnionContract &&
    timeOffType &&
    TOR_SUBMISSION_CUTOFF_TIME_REMINDER[empUnionContract][timeOffType]
      .getMaxSubmissionShiftDate(station)
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      // Plus 1 day for end date validation to cover shifts crossing midnight
      .plus({ day: 1 });

  if (input === null) {
    return END_DATE_IS_REQUIRED;
  }
  if (startDate === null) {
    return "";
  }

  const startDateVal = set_time_to_midnight(startDate);
  const endDateVal = set_time_to_midnight(input);

  var today = DateTime.now()
    .setZone(getTimeZoneFromSwaStation(station))
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  if (endDateVal.toMillis() < today.toMillis()) {
    return "End Date cannot be in the past";
  }

  if (endDateVal.toMillis() < startDateVal.toMillis()) {
    return INVALID_END_DATE;
  }

  if (maxDate < endDateVal) {
    return INVALID_END_DATE;
  }

  return "";
}

export function TOR_validateShiftStartTime(timeOffType, startDate, startHour, startMin, endHour, endMin, station) {
  // It's expected that both hourInput and minInput have been touched when
  // this validation is called.
  if (startHour.toString().trim() === "" || startMin.toString().trim() === "") {
    if (isEADorEDUorSEAD(timeOffType) && endHour.toString().trim() === "" && endMin.toString().trim() === "") {
      return "";
    } else {
      return SHIFT_TIME_IS_REQUIRED;
    }
  }
  if (startDate === null) {
    // This is to protect against NPE if user touches
    // start times but doesn't select a valid input
    // (start time input validation will catch this for the user)
    return "";
  }
  const validateShiftTimeErrorMsg = TOR_validateShiftStartTimePeriod(
    station,
    startDate,
    startHour,
    startMin,
    timeOffType
  );
  if (validateShiftTimeErrorMsg !== "") {
    return validateShiftTimeErrorMsg;
  }
  return "";
}

export function TOR_validateShiftStartTimePeriod(station, startDate, hourInput, minInput, timeOffType) {
  let today = DateTime.now().setZone(getTimeZoneFromSwaStation(station));

  const shiftTime = startDate.set({ hour: hourInput, minute: minInput });
  if (startDate.year === today.year && startDate.month === today.month && startDate.day === today.day) {
    if (shiftTime.toMillis() < today.toMillis() && !isLWOPTOWOPRequest(timeOffType)) {
      return "Shift Time cannot be in the past";
    }
  }
  return "";
}

export function TOR_validateShiftEndTime(
  timeOffType,
  startDate,
  endDate,
  startHourInput,
  startMinInput,
  endHourInput,
  endMinInput,
  station
) {
  // It's expected that both hourInputs and both minInputs have been touched when
  // this validation is called.
  if (endHourInput.toString().trim() === "" || endMinInput.toString().trim() === "") {
    if (
      isEADorEDUorSEAD(timeOffType) &&
      startHourInput.toString().trim() === "" &&
      startMinInput.toString().trim() === ""
    ) {
      return "";
    } else {
      return SHIFT_TIME_IS_REQUIRED;
    }
  }
  if (
    startHourInput.toString().trim() === "" ||
    startMinInput.toString().trim() === "" ||
    startDate === null ||
    endDate === null
  ) {
    // This is to protect against NPE if user touches
    // start times but doesn't select a valid input
    // (start time input validation will catch this for the user)
    return "";
  }

  var startDateVal = setStartDateTime(startDate, startHourInput, startMinInput, station);
  var endDateVal = setEndDateTime(endDate, endHourInput, endMinInput, station);
  var midnightForGivenDate = setTimeToMidnightForEADorEDUorSEAD(endDate, station);

  if (endDateVal.toMillis() <= startDateVal.toMillis()) {
    if (isEADorEDUorSEAD(timeOffType) && endDateVal.equals(startDateVal) && endDateVal.equals(midnightForGivenDate)) {
      return "";
    } else {
      return "Invalid End Time";
    }
  }

  if (isLWOPTOWOPRequest(timeOffType)) {
    const today = DateTime.now().setZone(getTimeZoneFromSwaStation(station));
    const shiftTime = endDate.set({ hour: endHourInput, minute: endMinInput });
    if (endDate.year === today.year && endDate.month === today.month && endDate.day === today.day) {
      if (shiftTime.toMillis() < today.toMillis()) {
        return "Shift Time cannot be in the past";
      }
    }
    return "";
  }

  return "";
}

export function TOR_validateShiftOneHourDeadline(shiftDate, shiftHour, shiftMinute, station) {
  let today = DateTime.now().setZone(getTimeZoneFromSwaStation(station));
  if (shiftDate === null || shiftHour === "" || shiftMinute === "") {
    // This is to protect against NPE if user touches
    // start times but doesn't select a valid input
    // (start time input validation will catch this for the user)
    return "";
  }

  const shiftTime = shiftDate.set({ hour: shiftHour, minute: shiftMinute });

  let oneHourFrom = shiftTime.minus({ hours: 1 });
  if (today > oneHourFrom) {
    return "Shift time past 1-hour deadline";
  }
  return "";
}

export function validateOwnTimeOffRequestEmployeeId(input, userId) {
  if (userId === input) {
    return "You cannot create time off for yourself";
  }
  return "";
}

export const isDuplicateTimeOffRequest = (timeOffRequestList, currentObj) => {
  let isDuplicate = false;

  const currentStartDate = currentObj.shiftStartDateTime;
  const currentEndDate = currentObj.shiftEndDateTime;

  const response = timeOffRequestList.filter((ele) => {
    const shiftStartDate = ele.shiftStartDateTime;
    const shiftEndDate = ele.shiftEndDateTime;

    if (ele.id === currentObj.id) {
      timeOffRequestList.filter((otherRequest) => {
        if (ele.id !== otherRequest.id) {
          return shiftStartDate.equals(currentStartDate) && shiftEndDate.equals(currentEndDate);
        } else {
          return false;
        }
      });
    } else {
      return shiftStartDate.equals(currentStartDate) && shiftEndDate.equals(currentEndDate);
    }
    return isDuplicate;
  });

  if (response?.length > 0) {
    isDuplicate = true;
  }
  return isDuplicate;
};

export function validateEmployeeTORShiftStartSubmissionDeadline(shiftStart, actionSelected, station) {
  if (isHourSubmissionDeadlineRequired(actionSelected)) {
    if (isDateTimeWithinOneHour(shiftStart, station)) {
      switch (actionSelected) {
        case ACTION_CANCEL_SUBMIT:
          return YOU_CANNOT_CANCEL_TOR_PAST_DEADLINE_ONE_HOUR_BEFORE_SHIFT_MESSAGE;
        case ACTION_RETRACT:
          return YOU_CANNOT_RETRACT_TOR_PAST_DEADLINE_ONE_HOUR_BEFORE_SHIFT_MESSAGE;
        default:
          return UNKNOWN_ACTION_MESSAGE(actionSelected);
      }
    }
  }

  return "";
}

export function validateEmployeeWOPDeadline(timeOffType, station, shiftStart, shiftEnd, actionSelected) {
  if (isWOPPastDeadline(timeOffType, shiftStart, shiftEnd, station)) {
    switch (actionSelected) {
      case ACTION_CANCEL_SUBMIT:
        return YOU_CANNOT_CANCEL_TOR_PAST_SHIFT_END_TIME_MESSAGE;
      case ACTION_RETRACT:
        return YOU_CANNOT_RETRACT_TOR_PAST_SHIFT_END_TIME_MESSAGE;
      default:
        return UNKNOWN_ACTION_MESSAGE(actionSelected);
    }
  }
  return "";
}

export function validateEmployeeTORCancelRequest(timeOffRequestType, actionSelected, userJobCode) {
  const isUserIAMContract = getUnionContractGivenWorkgroup(getWorkgroupGivenJobCode(userJobCode)) === IAM_CONTRACT;
  const isCancelRequest = actionSelected === ACTION_CANCEL_SUBMIT;
  if (
    isUserIAMContract &&
    isCancelRequest &&
    (timeOffRequestType === EDU_TIME_OFF_TYPE || timeOffRequestType === EAD_TIME_OFF_TYPE)
  ) {
    return YOU_CANNOT_CANCEL_AN_APPROVED_EAD_EDY_MESSAGE;
  }

  return "";
}

export function validateAdminTORShiftStartSubmissionDeadline(shiftStart, actionSelected, station) {
  if (isShiftStartSubmissionDeadlineRequired(actionSelected)) {
    if (isDateTimeInPast(shiftStart, station)) {
      switch (actionSelected) {
        case ACTION_APPROVE:
          return YOU_CANNOT_APPROVE_TOR_PAST_DEADLINE_START_TIME_OF_SHIFT_MESSAGE;
        case ACTION_REFUSE:
          return YOU_CANNOT_REFUSE_TOR_PAST_DEADLINE_START_TIME_OF_SHIFT_MESSAGE;
        case ACTION_CANCEL_APPROVE:
          return YOU_CANNOT_APPROVE_CANCEL_TOR_PAST_DEADLINE_START_TIME_OF_SHIFT_MESSAGE;
        case ACTION_CANCEL_REFUSE:
          return YOU_CANNOT_REFUSE_CANCEL_TOR_PAST_DEADLINE_START_TIME_OF_SHIFT_MESSAGE;
        default:
          return UNKNOWN_ACTION_MESSAGE(actionSelected);
      }
    }
  }

  if (isTwoMonthsFromShiftStartSubmissionDeadlineRequired(actionSelected)) {
    if (isDateTimeGreaterThanTwoMonthsInPast(shiftStart, station)) {
      switch (actionSelected) {
        case ACTION_CANCEL:
          return YOU_CANNOT_CANCEL_TOR_OVER_TWO_MONTHS_IN_PAST;
        case ACTION_REINSTATE:
          return YOU_CANNOT_REINSTATE_TOR_OVER_TWO_MONTHS_IN_PAST;
        default:
          return UNKNOWN_ACTION_MESSAGE(actionSelected);
      }
    }
  }

  return "";
}

export function validateAdminWOPDeadline(timeOffType, station, shiftStart, shiftEnd, actionSelected) {
  if (isWOPPastDeadline(timeOffType, shiftStart, shiftEnd, station)) {
    switch (actionSelected) {
      case ACTION_APPROVE:
        return YOU_CANNOT_APPROVE_TOR_PAST_DEADLINE_MESSAGE;
      case ACTION_REFUSE:
        return YOU_CANNOT_REFUSE_TOR_PAST_DEADLINE_MESSAGE;
      case ACTION_CANCEL:
        return YOU_CANNOT_CANCEL_TOR_PAST_SHIFT_END_TIME_MESSAGE;
      case ACTION_CANCEL_APPROVE:
        return YOU_CANNOT_APPROVE_CANCEL_TOR_PAST_DEADLINE_MESSAGE;
      case ACTION_CANCEL_REFUSE:
        return YOU_CANNOT_REFUSE_CANCEL_TOR_PAST_DEADLINE_MESSAGE;
      default:
        return UNKNOWN_ACTION_MESSAGE(actionSelected);
    }
  }
  return "";
}

export function validateIfAdminAttemptsActionOnTheirOwnTOR(userSessionEid, torEid, actionSelected) {
  if (userSessionEid === torEid) {
    switch (actionSelected) {
      case ACTION_APPROVE:
        return YOU_CANNOT_APPROVE_YOUR_OWN_TOR;
      case ACTION_REFUSE:
        return YOU_CANNOT_REFUSE_YOUR_OWN_TOR;
      case ACTION_CANCEL_APPROVE:
        return YOU_CANNOT_APPROVE_YOUR_OWN_TOR_CANCELLATION;
      case ACTION_CANCEL_REFUSE:
        return YOU_CANNOT_REFUSE_YOUR_OWN_TOR_CANCELLATION;
      case ACTION_CANCEL:
        return YOU_CANNOT_CANCEL_YOUR_OWN_TOR_FROM_TOR_ADMIN_SCREEN;
      case ACTION_REINSTATE:
        return YOU_CANNOT_REINSTATE_YOUR_OWN_TOR;
      default:
        return UNKNOWN_ACTION_MESSAGE(actionSelected);
    }
  }

  return "";
}

export function validateEmployeeTorStartDateFilter(input, endDateFilter, station) {
  if (input === null) {
    return START_DATE_IS_REQUIRED;
  }
  const startDateVal = set_time_to_midnight(input);

  const endDateVal = set_time_to_midnight(endDateFilter);

  if (endDateVal.toMillis() < startDateVal.toMillis()) {
    // end date is before start date
    return "End Date cannot be prior to Start Date";
  }
  if (startDateVal.toMillis() < THREE_YEARS_IN_PAST(station).toMillis()) {
    return "Start Date cannot be prior to 3 years in the past";
  }
  if (endDateVal.toMillis() > ONE_YEAR_FROM(station, startDateVal).toMillis()) {
    return "Start Date cannot be over 1 year before End Date";
  }

  return "";
}

export function validateEmployeeTorEndDateFilter(input, startDateFilter, station) {
  if (input === null) {
    return END_DATE_IS_REQUIRED;
  }
  const endDateVal = set_time_to_midnight(input);
  const startDateVal = set_time_to_midnight(startDateFilter);
  if (endDateVal.toMillis() < startDateVal.toMillis()) {
    // end date is before start date
    return "End Date cannot be prior to Start Date";
  }
  if (endDateVal.toMillis() > ONE_YEAR_FROM(station, startDateVal).toMillis()) {
    return "End Date cannot be over 1 year after Start Date";
  }
  return "";
}
